import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Plus,
  PlusCircle,
  Search,
  UserCheck,
  X
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Toast } from './ui/Toast';
import { LongToast, shouldUseLongToast } from './ui/LongToast';
import ToastPortal from './ToastPortal';
import { api } from '../services/api';

// Taille commune aux boutons du pied : un bouton de retour plus petit que celui
// qui avance se lit comme une hierarchie qui n'existe pas.
const BOUTON_PIED_CLASSES = 'h-9 min-w-[168px] justify-center gap-2';

// Largeur fixe : les noms de classe se repetent d'une carte a l'autre.
const BADGE_CLASSE_CLASSES = 'inline-flex items-center justify-center w-[104px] h-6 rounded bg-blue-50 text-[#246990] border border-blue-100 text-xs font-medium whitespace-nowrap px-2';

const STEPS = [
  'Famille',
  'Élèves',
  'Réductions',
  'Validation'
];

const formatMontant = (montant) =>
  `${new Intl.NumberFormat('fr-FR').format(Math.round(Number(montant || 0)))} FCFA`;

const getReductionLabel = (type) => {
  if (type === 'personnalisee') return 'Personnalisée';
  if (type === 'exceptionnelle') return 'Exceptionnelle';
  if (type === 'mixte') return 'Mixte';
  if (type === 'famille') return 'Famille';
  return 'Aucune';
};

const getDecisionLabel = (mode) => {
  if (mode === 'conserver') return 'Réduction actuelle conservée';
  if (mode === 'famille') return 'Valeur famille';
  if (mode === 'personnalisee') return 'Nouvelle réduction';
  return 'Aucune réduction principale';
};

// La reduction exceptionnelle est la seule qui se CUMULE : elle s'ajoute a la
// reduction principale (famille ou personnalisee) au lieu de la remplacer. Elle
// vit sur l'eleve depuis son inscription et la constitution de la famille ne la
// touche plus, sauf demande explicite du gestionnaire.
const getExceptionnelle = (eleve) => ({
  inscription: Number(eleve?.reduction_exceptionnelle_inscription || 0),
  mensuel: Number(eleve?.reduction_exceptionnelle_mensuel || 0),
  motif: String(eleve?.reduction_exceptionnelle_motif || '').trim()
});

const aUneExceptionnelle = (eleve) => {
  const exceptionnelle = getExceptionnelle(eleve);
  return exceptionnelle.inscription > 0 || exceptionnelle.mensuel > 0;
};

const getInitialDecision = (eleve, familleForm) => {
  const hasReduction = Number(eleve.reduction_inscription_actuelle || 0) > 0
    || Number(eleve.reduction_mensuel_actuelle || 0) > 0;
  const principaleInscription = Number(eleve.reduction_custom_inscription || 0);
  const principaleMensuel = Number(eleve.reduction_custom_mensuel || 0);

  return {
    mode_reduction: hasReduction ? 'conserver' : 'famille',
    // On pre-remplit avec la seule reduction PRINCIPALE. Y mettre le total actuel
    // ferait ressaisir l'exceptionnelle en personnalisee, donc la compter deux fois.
    reduction_inscription: principaleInscription || Number(familleForm.reduction_inscription || 0),
    reduction_mensuel: principaleMensuel || Number(familleForm.reduction_mensuel || 0),
    motif: '',
    retirer_exceptionnelle: false
  };
};

const ModalCreerFamilleDepuisEleves = ({ anneeScolaireId, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    nom: '',
    responsable_nom: '',
    telephones: [''],
    adresse: '',
    motif_creation: '',
    description: '',
    reduction_inscription: 0,
    reduction_mensuel: 0
  });
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEleves, setTotalEleves] = useState(0);
  const [eleves, setEleves] = useState([]);
  const [selectedMap, setSelectedMap] = useState({});
  const [decisions, setDecisions] = useState({});
  const [loadingEleves, setLoadingEleves] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const selectedEleves = useMemo(() => Object.values(selectedMap), [selectedMap]);

  const getDecisionAmounts = (eleve, decisionOverride = null) => {
    const decision = decisionOverride || decisions[eleve.id] || getInitialDecision(eleve, form);
    const fraisInscription = Number(eleve.frais_inscription_normal || 0);
    const fraisMensuel = Number(eleve.frais_mensuel_normal || 0);
    const exceptionnelle = getExceptionnelle(eleve);
    const garderExceptionnelle = !decision.retirer_exceptionnelle;

    // Reduction principale : une seule des trois sources, elles s'excluent.
    let principaleInscription = 0;
    let principaleMensuel = 0;

    if (decision.mode_reduction === 'conserver') {
      principaleInscription = Number(eleve.reduction_custom_inscription || 0);
      principaleMensuel = Number(eleve.reduction_custom_mensuel || 0);
    } else if (decision.mode_reduction === 'famille') {
      principaleInscription = Number(form.reduction_inscription || 0);
      principaleMensuel = Number(form.reduction_mensuel || 0);
    } else if (decision.mode_reduction === 'personnalisee') {
      principaleInscription = Number(decision.reduction_inscription || 0);
      principaleMensuel = Number(decision.reduction_mensuel || 0);
    }

    const exceptionnelleInscription = garderExceptionnelle ? exceptionnelle.inscription : 0;
    const exceptionnelleMensuel = garderExceptionnelle ? exceptionnelle.mensuel : 0;

    const reductionInscription = Math.max(0, Math.min(fraisInscription, principaleInscription + exceptionnelleInscription));
    const reductionMensuel = Math.max(0, Math.min(fraisMensuel, principaleMensuel + exceptionnelleMensuel));

    return {
      frais_inscription: fraisInscription,
      frais_mensuel: fraisMensuel,
      reduction_principale_inscription: principaleInscription,
      reduction_principale_mensuel: principaleMensuel,
      reduction_exceptionnelle_inscription: exceptionnelleInscription,
      reduction_exceptionnelle_mensuel: exceptionnelleMensuel,
      reduction_inscription: reductionInscription,
      reduction_mensuel: reductionMensuel,
      reduction_plafonnee: (principaleInscription + exceptionnelleInscription) > fraisInscription
        || (principaleMensuel + exceptionnelleMensuel) > fraisMensuel,
      inscription_a_payer: Math.max(0, fraisInscription - reductionInscription),
      mensuel_a_payer: Math.max(0, fraisMensuel - reductionMensuel),
      mensuel_actuel: Number(eleve.montant_mensuel_actuel || 0),
      inscription_actuelle: Number(eleve.montant_inscription_actuel || 0)
    };
  };

  // Combien d'enfants reprendront effectivement la valeur famille : c'est la
  // seule chose qui rend cette valeur utile.
  const valeurFamilleSaisie = Number(form.reduction_inscription || 0) > 0
    || Number(form.reduction_mensuel || 0) > 0;
  const nbSuivantValeurFamille = selectedEleves.filter((eleve) => (
    (decisions[eleve.id] || getInitialDecision(eleve, form)).mode_reduction === 'famille'
  )).length;

  // Les mois deja regles d'un eleve seul ne seront pas retouches par la creation.
  // La liste des eleves disponibles les fournit deja nommes : autant les montrer
  // plutot que d'enoncer la regle.
  const moisConservesDe = (eleve) => {
    const payes = Array.isArray(eleve.paiements?.mois_payes_noms) ? eleve.paiements.mois_payes_noms : [];
    const partiels = Array.isArray(eleve.paiements?.mois_partiels_noms) ? eleve.paiements.mois_partiels_noms : [];
    return [...payes, ...partiels];
  };

  const hasSensitiveChanges = useMemo(() => {
    const referenceHasReduction = Number(form.reduction_inscription || 0) > 0
      || Number(form.reduction_mensuel || 0) > 0;

    return referenceHasReduction || selectedEleves.some((eleve) => {
      const decision = decisions[eleve.id] || getInitialDecision(eleve, form);
      const amounts = getDecisionAmounts(eleve, decision);
      return Number(amounts.reduction_inscription || 0) !== Number(eleve.reduction_inscription_actuelle || 0)
        || Number(amounts.reduction_mensuel || 0) !== Number(eleve.reduction_mensuel_actuelle || 0)
        || Number(amounts.mensuel_a_payer || 0) !== Number(eleve.montant_mensuel_actuel || 0);
    });
  }, [selectedEleves, decisions, form]);

  useEffect(() => {
    if (step !== 2) return;
    const timer = setTimeout(() => {
      loadEleves();
    }, 250);
    return () => clearTimeout(timer);
  }, [step, search, page, anneeScolaireId]);

  const loadEleves = async () => {
    setLoadingEleves(true);
    setError('');
    try {
      const result = await api.familles.getElevesDisponiblesConstitution(anneeScolaireId, {
        search,
        page,
        limit: 8
      });
      if (result.success) {
        setEleves(result.data || []);
        setTotalPages(result.pagination?.totalPages || 1);
        setTotalEleves(result.pagination?.total || 0);
      } else {
        setError(result.message || 'Impossible de charger les élèves disponibles.');
      }
    } catch (err) {
      setError('Erreur lors du chargement des élèves disponibles.');
    } finally {
      setLoadingEleves(false);
    }
  };

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateTelephone = (index, value) => {
    setForm((current) => {
      const telephones = [...(current.telephones?.length ? current.telephones : [''])];
      telephones[index] = value;
      return { ...current, telephones };
    });
  };

  const addTelephone = () => {
    setForm((current) => {
      const telephones = current.telephones?.length ? current.telephones : [''];
      if (telephones.length >= 2) return current;
      return { ...current, telephones: [...telephones, ''] };
    });
  };

  const removeTelephone = (index) => {
    setForm((current) => {
      const telephones = (current.telephones?.length ? current.telephones : ['']).filter((_, phoneIndex) => phoneIndex !== index);
      return { ...current, telephones: telephones.length ? telephones : [''] };
    });
  };

  const toggleEleve = (eleve) => {
    setSelectedMap((current) => {
      const next = { ...current };
      if (next[eleve.id]) {
        delete next[eleve.id];
        setDecisions((decisionsCurrent) => {
          const decisionsNext = { ...decisionsCurrent };
          delete decisionsNext[eleve.id];
          return decisionsNext;
        });
      } else {
        next[eleve.id] = eleve;
        setDecisions((decisionsCurrent) => ({
          ...decisionsCurrent,
          [eleve.id]: getInitialDecision(eleve, form)
        }));
      }
      return next;
    });
  };

  const updateDecision = (eleveId, patch) => {
    setDecisions((current) => ({
      ...current,
      [eleveId]: {
        ...(current[eleveId] || {}),
        ...patch
      }
    }));
  };

  const validateStep = () => {
    setError('');
    if (step === 1) {
      if (!form.nom.trim()) {
        setError('Le nom de la famille est requis.');
        return false;
      }
      if (!form.motif_creation.trim()) {
        setError('Le motif de création est requis.');
        return false;
      }
    }

    if (step === 2 && selectedEleves.length < 2) {
      setError('Sélectionnez au moins deux élèves pour constituer une famille nombreuse.');
      return false;
    }

    return true;
  };

  const goNext = () => {
    if (!validateStep()) return;
    setStep((current) => Math.min(4, current + 1));
  };

  const goPrevious = () => {
    setError('');
    setStep((current) => Math.max(1, current - 1));
  };

  const submit = async () => {
    if (!validateStep()) return;
    setSaving(true);
    setError('');
    try {
      const telephones = (form.telephones || [])
        .map((telephone) => String(telephone || '').trim())
        .filter(Boolean)
        .slice(0, 2);
      const payload = {
        annee_scolaire_id: anneeScolaireId,
        nom: form.nom.trim(),
        responsable_nom: form.responsable_nom.trim(),
        telephone: telephones[0] || '',
        telephone_secondaire: telephones[1] || null,
        telephones,
        adresse: form.adresse.trim(),
        motif_creation: form.motif_creation.trim(),
        description: form.description.trim(),
        reduction_inscription: Number(form.reduction_inscription || 0),
        reduction_mensuel: Number(form.reduction_mensuel || 0),
        eleves: selectedEleves.map((eleve) => ({
          eleve_id: eleve.id,
          ...(decisions[eleve.id] || getInitialDecision(eleve, form))
        }))
      };

      const result = await api.familles.constituerDepuisEleves(payload);
      if (result.success) {
        onSuccess?.({
          ...(result.data || {}),
          validation_required: Boolean(result.validation_required)
        });
        return;
      }
      setError(result.message || "La famille n'a pas pu être créée.");
    } catch (err) {
      setError('Erreur lors de la création de la famille.');
    } finally {
      setSaving(false);
    }
  };

  const totals = useMemo(() => {
    return selectedEleves.reduce((acc, eleve) => {
      const decision = decisions[eleve.id] || getInitialDecision(eleve, form);
      const amounts = getDecisionAmounts(eleve, decision);
      acc.inscriptionReelle += amounts.frais_inscription;
      acc.inscriptionReduction += amounts.reduction_inscription;
      acc.inscription += amounts.inscription_a_payer;
      acc.mensuelReel += amounts.frais_mensuel;
      acc.mensuelReduction += amounts.reduction_mensuel;
      acc.mensuel += amounts.mensuel_a_payer;
      return acc;
    }, {
      inscriptionReelle: 0,
      inscriptionReduction: 0,
      inscription: 0,
      mensuelReel: 0,
      mensuelReduction: 0,
      mensuel: 0
    });
  }, [selectedEleves, decisions, form]);

  const renderStepIndicator = () => (
    <div className="px-6 pt-5">
      <div className="flex items-center justify-center gap-3">
        {STEPS.map((label, index) => {
          const number = index + 1;
          const active = step === number;
          const done = step > number;
          return (
            <div key={label} className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                done ? 'bg-[#319190] text-white' : active ? 'bg-[#246990] text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {done ? <Check className="w-4 h-4" /> : number}
              </div>
              <span className={`hidden md:inline text-sm font-medium ${active ? 'text-[#246990]' : 'text-gray-500'}`}>
                {label}
              </span>
              {number < STEPS.length && <div className="hidden md:block w-10 h-px bg-gray-200" />}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderStepOne = () => (
    <div className="space-y-5">
      <div className="bg-[#eef7fb] border border-[#246990]/20 rounded-lg p-4">
        <div>
          <div>
            <h3 className="font-semibold text-[#246990]">Nouvelle famille depuis des élèves seuls</h3>
            <p className="text-sm text-gray-600 mt-1">
              Les élèves sélectionnés étaient suivis individuellement. Les paiements déjà effectués ne seront pas modifiés.
              À partir de la création de la famille, le suivi des prochains paiements se fera depuis Familles nombreuses.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="space-y-1.5">
          <span className="text-sm font-medium text-gray-700">Nom de la famille</span>
          <input
            value={form.nom}
            onChange={(event) => updateForm('nom', event.target.value)}
            placeholder="Ex. Famille MBOUA"
            className="w-full rounded-lg border border-gray-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#246990]/20 focus:border-[#246990]"
          />
        </label>
      </div>

      {/* Un seul champ libre plutot que deux. « Motif » et « note interne » posaient
          la meme question a deux endroits : le second finissait rempli de « RAS ». */}
      <label className="space-y-1.5 block">
        <span className="text-sm font-medium text-gray-700">
          Motif et contexte de la création <span className="text-red-500">*</span>
        </span>
        <textarea
          value={form.motif_creation}
          onChange={(event) => updateForm('motif_creation', event.target.value)}
          placeholder="Ex. Regroupement familial confirmé par le parent, fratrie déjà inscrite séparément…"
          rows={4}
          className="w-full rounded-lg border border-gray-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#246990]/20 focus:border-[#246990] resize-none"
        />
        <span className="block text-[11px] text-gray-500">
          Conservé dans le dossier de la famille et lu par le validateur.
        </span>
      </label>

      <div className="rounded-lg border border-[#246990]/15 bg-white p-4">
        <div className="mb-3">
          <h4 className="text-sm font-semibold text-[#246990]">Coordonnées de la famille</h4>
          <p className="text-xs text-gray-500 mt-0.5">
            Ces informations seront reprises dans le dossier de la famille et restent modifiables après validation.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="space-y-1.5">
            <span className="text-sm font-medium text-gray-700">Responsable de la famille</span>
            <input
              value={form.responsable_nom}
              onChange={(event) => updateForm('responsable_nom', event.target.value)}
              placeholder="Nom du parent ou tuteur responsable"
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#246990]/20 focus:border-[#246990]"
            />
          </label>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-gray-700">Téléphones parent / responsable</span>
              {(form.telephones || []).length < 2 && (
                <button
                  type="button"
                  onClick={addTelephone}
                  className="inline-flex items-center gap-1 text-xs font-medium text-[#246990] hover:text-[#1b5576]"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Ajouter
                </button>
              )}
            </div>
            <div className="space-y-2">
              {(form.telephones?.length ? form.telephones : ['']).map((telephone, phoneIndex) => (
                <div key={phoneIndex} className="flex items-center gap-2">
                  <input
                    value={telephone}
                    onChange={(event) => updateTelephone(phoneIndex, event.target.value)}
                    placeholder={phoneIndex === 0 ? 'Numéro principal' : 'Numéro secondaire'}
                    className="min-w-0 flex-1 rounded-lg border border-gray-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#246990]/20 focus:border-[#246990]"
                  />
                  {(form.telephones || []).length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeTelephone(phoneIndex)}
                      className="h-10 w-10 inline-flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
                      title="Retirer ce numéro"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <p className="text-[11px] text-gray-500">
              Deux numéros maximum. Saisir chaque contact dans un champ séparé.
            </p>
          </div>
          <label className="space-y-1.5">
            <span className="text-sm font-medium text-gray-700">Adresse</span>
            <input
              value={form.adresse}
              onChange={(event) => updateForm('adresse', event.target.value)}
              placeholder="Ex. Quartier, rue, avenue..."
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#246990]/20 focus:border-[#246990]"
            />
          </label>
        </div>
      </div>

    </div>
  );

  const renderEleveCard = (eleve) => {
    const selected = Boolean(selectedMap[eleve.id]);
    const paidMonths = Array.isArray(eleve.paiements?.mois_payes_noms) ? eleve.paiements.mois_payes_noms : [];
    const partialMonths = Array.isArray(eleve.paiements?.mois_partiels_noms) ? eleve.paiements.mois_partiels_noms : [];
    return (
      <button
        key={eleve.id}
        type="button"
        onClick={() => toggleEleve(eleve)}
        className={`w-full text-left rounded-lg border p-3 transition-colors ${
          selected ? 'border-[#246990] bg-[#eef7fb]' : 'border-gray-200 bg-white hover:border-[#246990]/40'
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900">{eleve.prenom} {eleve.nom}</span>
              <span className={BADGE_CLASSE_CLASSES}>{eleve.classe_nom || 'Classe non renseignée'}</span>
              <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-xs font-medium">{eleve.matricule}</span>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Parent : {eleve.parents_noms || 'Non renseigné'} {eleve.parents_telephones ? `- ${eleve.parents_telephones}` : ''}
            </div>
          </div>
          <div className={`w-6 h-6 rounded-full border flex items-center justify-center ${
            selected ? 'bg-[#246990] border-[#246990] text-white' : 'border-gray-300 text-transparent'
          }`}>
            <Check className="w-4 h-4" />
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-6 gap-2 mt-3 text-xs">
          <div className="rounded bg-gray-50 px-2 py-1.5">
            <span className="text-gray-500 block">Réduction</span>
            <strong className="text-[#246990]">{getReductionLabel(eleve.type_reduction)}</strong>
          </div>
          <div className="rounded bg-gray-50 px-2 py-1.5">
            <span className="text-gray-500 block">Mensuel réel</span>
            <strong>{formatMontant(eleve.frais_mensuel_normal)}</strong>
          </div>
          <div className="rounded bg-gray-50 px-2 py-1.5">
            <span className="text-gray-500 block">Réduction mensuelle</span>
            <strong className="text-emerald-700">-{formatMontant(eleve.reduction_mensuel_actuelle)}</strong>
          </div>
          <div className="rounded bg-gray-50 px-2 py-1.5">
            <span className="text-gray-500 block">Mensuel actuel</span>
            <strong>{formatMontant(eleve.montant_mensuel_actuel)}</strong>
          </div>
          <div className="rounded bg-gray-50 px-2 py-1.5">
            <span className="text-gray-500 block">Mois payés</span>
            <strong>{eleve.paiements?.mois_payes || 0} mois</strong>
            <span className="block text-[11px] text-gray-500 truncate" title={paidMonths.join(', ')}>
              {paidMonths.length ? paidMonths.join(', ') : 'Aucun mois payé'}
            </span>
            {partialMonths.length > 0 && (
              <span className="block text-[11px] text-amber-700 truncate" title={partialMonths.join(', ')}>
                Partiel : {partialMonths.join(', ')}
              </span>
            )}
          </div>
          <div className="rounded bg-gray-50 px-2 py-1.5">
            <span className="text-gray-500 block">Reste annuel</span>
            <strong>{formatMontant(eleve.paiements?.reste_a_payer || 0)}</strong>
          </div>
        </div>
      </button>
    );
  };

  const renderStepTwo = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold text-gray-900">Élèves seuls disponibles</h3>
          <p className="text-sm text-gray-500">Seuls les élèves actifs sans famille sont proposés.</p>
        </div>
        <div className="px-3 py-1.5 rounded-lg bg-[#eef7fb] text-[#246990] text-sm font-semibold">
          {selectedEleves.length} sélectionné(s)
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          placeholder="Rechercher un élève, une classe ou un matricule…"
          className="w-full rounded-lg border border-gray-200 pl-10 pr-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#246990]/20 focus:border-[#246990]"
        />
      </div>

      {loadingEleves ? (
        <div className="py-12 flex items-center justify-center text-[#246990]">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Chargement des élèves…
        </div>
      ) : eleves.length === 0 ? (
        <div className="py-12 text-center rounded-lg border border-dashed border-gray-200 text-gray-500">
          Aucun élève seul disponible.
        </div>
      ) : (
        <div className="space-y-2 max-h-[390px] overflow-y-auto pr-1">
          {eleves.map(renderEleveCard)}
        </div>
      )}

      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>{totalEleves} élève(s) disponible(s)</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loadingEleves}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span>Page {page} / {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loadingEleves}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  const renderDecisionMode = (eleve, mode, title, subtitle) => {
    const decision = decisions[eleve.id] || getInitialDecision(eleve, form);
    const active = decision.mode_reduction === mode;
    return (
      <button
        type="button"
        onClick={() => updateDecision(eleve.id, {
          mode_reduction: mode,
          reduction_inscription: mode === 'famille'
            ? Number(form.reduction_inscription || 0)
            : mode === 'conserver'
              ? Number(eleve.reduction_custom_inscription || 0)
              : mode === 'aucune'
                ? 0
                : decision.reduction_inscription,
          reduction_mensuel: mode === 'famille'
            ? Number(form.reduction_mensuel || 0)
            : mode === 'conserver'
              ? Number(eleve.reduction_custom_mensuel || 0)
              : mode === 'aucune'
                ? 0
                : decision.reduction_mensuel
        })}
        className={`rounded-lg border px-3 py-2 text-left transition-colors ${
          active ? 'border-[#246990] bg-[#eef7fb]' : 'border-gray-200 bg-white hover:border-[#246990]/40'
        }`}
      >
        <div className="text-sm font-semibold text-gray-900">{title}</div>
        <div className="text-xs text-gray-500">{subtitle}</div>
      </button>
    );
  };

  // Poser une reduction personnalisee plus petite que la valeur de famille fait
  // PAYER PLUS l'eleve : la personnalisee remplace la valeur de famille, elle ne
  // s'y ajoute pas. Le piege se signale avant, avec deux montants absolus.
  const getAvertissementEviction = (eleve, decision, amounts) => {
    if (decision.mode_reduction !== 'personnalisee') return null;
    const valeurFamille = Number(form.reduction_mensuel || 0);
    const personnalisee = Number(decision.reduction_mensuel || 0);
    if (valeurFamille <= 0 || personnalisee >= valeurFamille) return null;

    const amountsValeurFamille = getDecisionAmounts(eleve, { ...decision, mode_reduction: 'famille' });
    return {
      valeurFamille,
      personnalisee,
      mensuelSiValeurFamille: amountsValeurFamille.mensuel_a_payer,
      mensuelSiPersonnalisee: amounts.mensuel_a_payer
    };
  };

  // Le meme piege dans l'autre sens, et c'est celui qu'on a constate en
  // production : Berger MAKAYA payait 13 000 avec sa reduction propre, et
  // reprendre la valeur famille l'a fait passer a 13 500 sans qu'aucun ecran ne
  // le dise. L'alerte ci-dessus ne couvre pas ce chemin, puisqu'elle ne regarde
  // que le mode « personnalisee ». Ici on compare ce que l'eleve paie
  // aujourd'hui a ce qu'il paiera, quel que soit le mode retenu.
  const getAvertissementHausse = (eleve, decision, amounts) => {
    const mensuelActuel = Number(eleve.montant_mensuel_actuel || 0);
    if (mensuelActuel <= 0) return null;
    if (Number(amounts.mensuel_a_payer || 0) <= mensuelActuel) return null;

    return {
      mensuelActuel,
      mensuelApres: Number(amounts.mensuel_a_payer || 0),
      raison: decision.mode_reduction === 'famille'
        ? 'la valeur famille remplace sa réduction propre'
        : decision.mode_reduction === 'aucune'
          ? 'sa réduction propre est retirée'
          : 'la réduction retenue est plus faible que la sienne'
    };
  };

  const renderStepThree = () => (
    <div className="space-y-4">
      <div className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-4">
        <h3 className="font-semibold text-[#246990]">Choix des réductions par élève</h3>
        <p className="text-sm text-gray-600 mt-1">
          Chaque élève garde sa propre règle. Les paiements déjà réglés restent intacts ; seuls les mois non entamés sont recalculés.
        </p>
      </div>

      {/* La valeur famille se saisit ici, pas a l'etape identite : elle n'a de sens
          qu'en face des enfants qui peuvent la reprendre. Demandee trop tot, elle
          finissait enregistree sans que personne ne la suive - 340 familles etaient
          dans ce cas. */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900">Valeur famille</h3>
          <span className="text-xs text-gray-500">Facultative</span>
        </div>
        <p className="text-xs text-gray-600 mt-1">
          Une réduction portée par la famille, que chaque enfant peut reprendre ou non. Ne la saisissez
          que si au moins l'un d'eux doit la suivre.
        </p>

        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="space-y-1.5 block text-sm">
            <span className="text-gray-700">Sur les frais de début d'année</span>
            <div className="relative">
              <input
                type="number"
                min="0"
                value={form.reduction_inscription}
                onChange={(event) => updateForm('reduction_inscription', event.target.value)}
                className="w-full h-9 rounded-lg border border-gray-200 px-3 pr-14 focus:outline-none focus:ring-2 focus:ring-[#246990]/20 focus:border-[#246990]"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">FCFA</span>
            </div>
          </label>
          <label className="space-y-1.5 block text-sm">
            <span className="text-gray-700">Sur les frais mensuels</span>
            <div className="relative">
              <input
                type="number"
                min="0"
                value={form.reduction_mensuel}
                onChange={(event) => updateForm('reduction_mensuel', event.target.value)}
                className="w-full h-9 rounded-lg border border-gray-200 px-3 pr-14 focus:outline-none focus:ring-2 focus:ring-[#246990]/20 focus:border-[#246990]"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">FCFA</span>
            </div>
          </label>
        </div>

        {valeurFamilleSaisie && (
          <div className={`mt-3 rounded-md border px-3 py-2 text-xs ${
            nbSuivantValeurFamille > 0
              ? 'border-[#cfe3ec] bg-[#f4f9fc] text-[#174866]'
              : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}>
            {nbSuivantValeurFamille > 0
              ? `${nbSuivantValeurFamille} enfant${nbSuivantValeurFamille > 1 ? 's' : ''} sur ${selectedEleves.length} suivra${nbSuivantValeurFamille > 1 ? 'ont' : ''} cette valeur.`
              : `Aucun des ${selectedEleves.length} enfants ne suit cette valeur : elle sera enregistrée sans servir à personne. Choisissez « Valeur famille » pour au moins l'un d'eux, ou laissez ces champs à zéro.`}
          </div>
        )}
      </div>

      <div className="space-y-3 max-h-[470px] overflow-y-auto pr-1">
        {selectedEleves.map((eleve) => {
          const decision = decisions[eleve.id] || getInitialDecision(eleve, form);
          const amounts = getDecisionAmounts(eleve, decision);
          const exceptionnelle = getExceptionnelle(eleve);
          const avertissement = getAvertissementEviction(eleve, decision, amounts);
          const avertissementHausse = getAvertissementHausse(eleve, decision, amounts);
          return (
            <div key={eleve.id} className="rounded-lg border border-gray-200 bg-white overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-3">
                <div>
                  <h4 className="font-semibold text-gray-900">{eleve.prenom} {eleve.nom}</h4>
                  <p className="text-xs text-gray-500">
                    Avant création : élève seul · {eleve.classe_nom} · réduction actuelle : {getReductionLabel(eleve.type_reduction)}
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-xs px-2 py-1 rounded bg-[#eef7fb] text-[#246990] font-semibold block">
                    Après choix : {formatMontant(amounts.mensuel_a_payer)}/mois
                  </span>
                  <span className="text-[11px] text-gray-500 block mt-1">
                    Actuel : {formatMontant(amounts.mensuel_actuel)}/mois
                  </span>
                </div>
              </div>

              <div className="p-4 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  {renderDecisionMode(eleve, 'conserver', 'Conserver', 'Garde la réduction actuelle de cet élève')}
                  {renderDecisionMode(eleve, 'famille', 'Valeur famille', 'Applique la valeur de la nouvelle famille')}
                  {renderDecisionMode(eleve, 'personnalisee', 'Nouvelle réduction', 'Un montant décidé pour cet élève seul')}
                  {renderDecisionMode(eleve, 'aucune', 'Aucune', 'Retire la valeur famille ou la réduction propre')}
                </div>

                {decision.mode_reduction === 'personnalisee' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-[#f8fbfd] border border-[#246990]/10 rounded-lg p-3">
                    <label className="space-y-1">
                      <span className="text-xs text-gray-500">Réduction inscription</span>
                      <input
                        type="number"
                        min="0"
                        value={decision.reduction_inscription}
                        onChange={(event) => updateDecision(eleve.id, { reduction_inscription: event.target.value })}
                        className="w-full rounded border border-gray-200 px-2 py-2 text-sm"
                      />
                    </label>
                    <label className="space-y-1">
                      <span className="text-xs text-gray-500">Réduction mensuelle</span>
                      <input
                        type="number"
                        min="0"
                        value={decision.reduction_mensuel}
                        onChange={(event) => updateDecision(eleve.id, { reduction_mensuel: event.target.value })}
                        className="w-full rounded border border-gray-200 px-2 py-2 text-sm"
                      />
                    </label>
                    <label className="space-y-1">
                      <span className="text-xs text-gray-500">Motif spécifique</span>
                      <input
                        value={decision.motif}
                        onChange={(event) => updateDecision(eleve.id, { motif: event.target.value })}
                        placeholder="Optionnel"
                        className="w-full rounded border border-gray-200 px-2 py-2 text-sm"
                      />
                    </label>
                  </div>
                )}

                {avertissement && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-amber-900">
                      <div className="font-semibold">Cette réduction remplace la valeur famille, elle ne s'y ajoute pas</div>
                      <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div className="rounded border border-amber-200 bg-white px-2.5 py-2">
                          <div className="text-[11px] text-gray-500">
                            En reprenant la valeur famille ({formatMontant(avertissement.valeurFamille)})
                          </div>
                          <div className="font-semibold text-gray-900">
                            {formatMontant(avertissement.mensuelSiValeurFamille)} / mois
                          </div>
                        </div>
                        <div className="rounded border border-amber-300 bg-white px-2.5 py-2">
                          <div className="text-[11px] text-gray-500">
                            Avec cette réduction propre ({formatMontant(avertissement.personnalisee)})
                          </div>
                          <div className="font-semibold text-gray-900">
                            {formatMontant(avertissement.mensuelSiPersonnalisee)} / mois
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {avertissementHausse && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-amber-900">
                      <div className="font-semibold">Ce choix fait payer cet élève davantage qu'aujourd'hui</div>
                      <div className="mt-0.5 text-xs text-amber-800">
                        Parce que {avertissementHausse.raison}.
                      </div>
                      <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div className="rounded border border-amber-200 bg-white px-2.5 py-2">
                          <div className="text-[11px] text-gray-500">Il paie aujourd'hui</div>
                          <div className="font-semibold text-gray-900">
                            {formatMontant(avertissementHausse.mensuelActuel)} / mois
                          </div>
                        </div>
                        <div className="rounded border border-amber-300 bg-white px-2.5 py-2">
                          <div className="text-[11px] text-gray-500">Après la création de la famille</div>
                          <div className="font-semibold text-gray-900">
                            {formatMontant(avertissementHausse.mensuelApres)} / mois
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {aUneExceptionnelle(eleve) && (
                  <div className="rounded-lg border border-violet-200 bg-violet-50 p-3">
                    <div className="flex items-start gap-3">
                      <PlusCircle className="w-5 h-5 text-violet-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-violet-900">
                          Réduction exceptionnelle déjà accordée à cet élève
                        </div>
                        <div className="text-xs text-violet-800 mt-1">
                          {exceptionnelle.mensuel > 0 && <span>{formatMontant(exceptionnelle.mensuel)} / mois</span>}
                          {exceptionnelle.mensuel > 0 && exceptionnelle.inscription > 0 && <span> - </span>}
                          {exceptionnelle.inscription > 0 && <span>{formatMontant(exceptionnelle.inscription)} sur l'inscription</span>}
                          {exceptionnelle.motif && <span className="text-violet-700"> - motif : {exceptionnelle.motif}</span>}
                        </div>
                        <p className="text-xs text-violet-700 mt-1.5">
                          Elle est conservée et s'ajoute au choix ci-dessus, quel qu'il soit. C'est la seule réduction
                          qui se cumule ; les montants affichés en tiennent déjà compte.
                        </p>
                        <label className="mt-2 inline-flex items-center gap-2 text-xs text-violet-900 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={Boolean(decision.retirer_exceptionnelle)}
                            onChange={(event) => updateDecision(eleve.id, { retirer_exceptionnelle: event.target.checked })}
                            className="w-4 h-4 rounded border-violet-300 text-violet-600 focus:ring-violet-500"
                          />
                          <span>Retirer cette réduction exceptionnelle en rejoignant la famille</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {amounts.reduction_plafonnee && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                    Le cumul des réductions dépasse les frais : la réduction est ramenée au montant des frais.
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Frais réels</div>
                    <div className="mt-2 flex justify-between gap-3">
                      <span>Inscription</span>
                      <strong>{formatMontant(amounts.frais_inscription)}</strong>
                    </div>
                    <div className="mt-1 flex justify-between gap-3">
                      <span>Mensuel</span>
                      <strong>{formatMontant(amounts.frais_mensuel)}</strong>
                    </div>
                  </div>
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                    <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Réduction appliquée</div>
                    <div className="mt-2 flex justify-between gap-3">
                      <span>Inscription</span>
                      <strong>-{formatMontant(amounts.reduction_inscription)}</strong>
                    </div>
                    <div className="mt-1 flex justify-between gap-3">
                      <span>Mensuel</span>
                      <strong>-{formatMontant(amounts.reduction_mensuel)}</strong>
                    </div>
                    {amounts.reduction_exceptionnelle_mensuel > 0 && (
                      <div className="mt-2 pt-2 border-t border-emerald-200 text-[11px] text-emerald-800">
                        dont {formatMontant(amounts.reduction_principale_mensuel)} au titre du choix ci-dessus
                        et {formatMontant(amounts.reduction_exceptionnelle_mensuel)} d'exceptionnelle
                      </div>
                    )}
                  </div>
                  <div className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-3">
                    <div className="text-xs uppercase tracking-wide text-[#246990] font-semibold">Montant après choix</div>
                    <div className="mt-2 flex justify-between gap-3">
                      <span>Inscription</span>
                      <strong>{formatMontant(amounts.inscription_a_payer)}</strong>
                    </div>
                    <div className="mt-1 flex justify-between gap-3">
                      <span>Mensuel</span>
                      <strong>{formatMontant(amounts.mensuel_a_payer)}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderStepFour = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-4">
          <span className="text-xs uppercase tracking-wide text-[#246990] font-semibold">Famille</span>
          <div className="text-lg font-semibold text-gray-900 mt-1">{form.nom}</div>
          <p className="text-xs text-gray-500 mt-1">{form.motif_creation}</p>
          {(form.responsable_nom || (form.telephones || []).some((telephone) => String(telephone || '').trim()) || form.adresse) && (
            <div className="mt-3 space-y-1 text-xs text-gray-600">
              {form.responsable_nom && (
                <div className="flex items-center gap-1.5">
                  <span>Responsable : {form.responsable_nom}</span>
                </div>
              )}
              {(form.telephones || [])
                .map((telephone) => String(telephone || '').trim())
                .filter(Boolean)
                .slice(0, 2)
                .map((telephone, index) => (
                  <div key={`${telephone}-${index}`} className="flex items-center gap-1.5">
                    <span>{index === 0 ? 'Principal' : 'Secondaire'} : {telephone}</span>
                  </div>
                ))}
              {form.adresse && (
                <div className="flex items-center gap-1.5">
                  <span>{form.adresse}</span>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="rounded-lg border border-[#319190]/20 bg-[#edf8f6] p-4">
          <span className="text-xs uppercase tracking-wide text-[#319190] font-semibold">Membres</span>
          <div className="text-xl font-semibold text-gray-900 mt-1">{selectedEleves.length}</div>
          <p className="text-xs text-gray-500 mt-1">Élèves seuls rattachés à la famille</p>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <span className="text-xs uppercase tracking-wide text-amber-700 font-semibold">Calcul famille</span>
          <div className="text-xs text-gray-600 mt-1">Frais réels moins réductions égale montant famille</div>
          <div className="text-sm font-semibold text-gray-900 mt-2">
            Inscription : {formatMontant(totals.inscriptionReelle)} - {formatMontant(totals.inscriptionReduction)} = {formatMontant(totals.inscription)}
          </div>
          <div className="text-sm font-semibold text-gray-900 mt-1">
            Mensuel : {formatMontant(totals.mensuelReel)} - {formatMontant(totals.mensuelReduction)} = {formatMontant(totals.mensuel)}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-4 text-sm text-gray-700">
        <span className="font-semibold text-[#246990]">Règle comptable appliquée :</span> les paiements déjà faits
        quand les élèves étaient seuls restent intacts dans Paiements scolarité. Les prochains mois non réglés
        seront recalculés selon les choix ci-dessous et suivis depuis la famille nombreuse.
      </div>

      {/* La regle enoncee ne dit pas quels mois. Ceux-la sont connus avant meme
          d'ecrire : la liste des eleves les fournit nommes. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="text-sm font-semibold text-gray-800 mb-2">Mois conservés</div>
          <p className="text-[11px] text-gray-500 mb-2">
            Déjà réglés ou entamés en gestion individuelle : ils gardent leur montant.
          </p>
          <div className="space-y-2 max-h-44 overflow-y-auto pr-1">
            {selectedEleves.every((eleve) => moisConservesDe(eleve).length === 0) ? (
              <div className="rounded-md border border-dashed border-gray-300 bg-white p-3 text-xs text-gray-500">
                Aucun mois réglé : tous les mois de l'année seront recalculés.
              </div>
            ) : (
              selectedEleves
                .filter((eleve) => moisConservesDe(eleve).length > 0)
                .map((eleve) => (
                  <div key={`conserve-${eleve.id}`} className="rounded-md border border-gray-200 bg-white p-2">
                    <div className="text-xs font-semibold text-gray-800">{eleve.prenom} {eleve.nom}</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {moisConservesDe(eleve).map((mois, index) => (
                        <span key={`${eleve.id}-${mois}-${index}`} className="rounded bg-gray-100 px-2 py-0.5 text-[10px] text-gray-700">
                          {mois}
                        </span>
                      ))}
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>

        <div className="rounded-lg border border-green-200 bg-green-50 p-3">
          <div className="text-sm font-semibold text-green-800 mb-2">Mois recalculés</div>
          <p className="text-[11px] text-green-700 mb-2">
            Non encore entamés : ils prendront le montant issu du choix fait à l'étape précédente.
          </p>
          <div className="space-y-2 max-h-44 overflow-y-auto pr-1">
            {selectedEleves.map((eleve) => {
              const amounts = getDecisionAmounts(eleve, decisions[eleve.id] || getInitialDecision(eleve, form));
              const nbConserves = moisConservesDe(eleve).length;
              const nbTotal = Number(eleve.paiements?.mois_total || 0);
              const nbRecalcules = Math.max(0, nbTotal - nbConserves);
              return (
                <div key={`recalcule-${eleve.id}`} className="rounded-md border border-green-100 bg-white/70 p-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-green-900">{eleve.prenom} {eleve.nom}</span>
                    <span className="text-xs font-semibold text-green-900">{formatMontant(amounts.mensuel_a_payer)}/mois</span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-green-800">
                    {nbTotal > 0
                      ? `${nbRecalcules} mois sur ${nbTotal}`
                      : 'Aucune ligne mensuelle enregistrée pour cet élève'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {hasSensitiveChanges && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-semibold">Validation de direction requise</div>
            <p className="mt-1">
              Les réductions ou montants demandés modifient la situation financière d'élèves déjà inscrits.
              La famille ne sera pas créée immédiatement : une demande sera envoyée au Centre de Support.
            </p>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 font-semibold text-gray-900">
          Élèves à rattacher
        </div>
        <div className="divide-y divide-gray-100 max-h-[360px] overflow-y-auto">
          {selectedEleves.map((eleve) => {
            const decision = decisions[eleve.id] || getInitialDecision(eleve, form);
            return (
              <div key={eleve.id} className="px-4 py-3 flex items-center justify-between gap-3">
                <div>
                  <div className="font-semibold text-gray-900">{eleve.prenom} {eleve.nom}</div>
                  <div className="text-xs text-gray-500">
                    Origine : élève seul · historique comptable individuel conservé
                  </div>
                </div>
                <div className="text-right text-sm">
                  {(() => {
                    const amounts = getDecisionAmounts(eleve, decision);
                    return (
                      <>
                        <div className="font-semibold text-[#246990]">{getDecisionLabel(decision.mode_reduction)}</div>
                        <div className="text-xs text-gray-500">{eleve.classe_nom}</div>
                        <div className="text-xs text-gray-600 mt-1">
                          Mensuel : {formatMontant(amounts.frais_mensuel)} - {formatMontant(amounts.reduction_mensuel)} = {formatMontant(amounts.mensuel_a_payer)}
                        </div>
                        <div className="text-xs text-gray-600">
                          Inscription : {formatMontant(amounts.frais_inscription)} - {formatMontant(amounts.reduction_inscription)} = {formatMontant(amounts.inscription_a_payer)}
                        </div>
                        {aUneExceptionnelle(eleve) && (
                          <div className={`text-xs mt-1 font-medium ${decision.retirer_exceptionnelle ? 'text-amber-700' : 'text-violet-700'}`}>
                            {decision.retirer_exceptionnelle
                              ? `Exceptionnelle de ${formatMontant(getExceptionnelle(eleve).mensuel)}/mois retirée`
                              : `Dont exceptionnelle conservée : ${formatMontant(getExceptionnelle(eleve).mensuel)}/mois`}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    if (step === 1) return renderStepOne();
    if (step === 2) return renderStepTwo();
    if (step === 3) return renderStepThree();
    return renderStepFour();
  };

  return (
    <>
    <Dialog open onOpenChange={(ouvert) => { if (!ouvert && !saving) onClose(); }}>
      <DialogContent
        className="w-[95vw] max-w-6xl !max-h-[94vh] overflow-hidden flex flex-col p-0 gap-0"
        onPointerDownOutside={(evenement) => evenement.preventDefault()}
      >
        <DialogHeader className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#246990] text-white flex items-center justify-center flex-shrink-0">
                <UserCheck className="w-5 h-5" />
              </div>
              <div className="text-left">
                <DialogTitle className="text-lg font-semibold text-gray-900">
                  Créer une famille nombreuse
                </DialogTitle>
                <p className="text-sm text-gray-500">
                  Constituer une famille à partir d'élèves déjà inscrits seuls.
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={saving}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </DialogHeader>

        {renderStepIndicator()}

        <div className="flex-1 overflow-y-auto p-6">
          {renderCurrentStep()}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-gray-200 bg-gray-50 sm:justify-between">
          <Button
            variant="outline"
            onClick={step === 1 ? onClose : goPrevious}
            disabled={saving}
            className={BOUTON_PIED_CLASSES}
          >
            {step === 1 ? <X className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
            {step === 1 ? 'Annuler' : 'Précédent'}
          </Button>

          {step < 4 ? (
            <Button
              onClick={goNext}
              disabled={saving}
              className={`${BOUTON_PIED_CLASSES} bg-[#246990] hover:bg-[#1f5a7a] text-white`}
            >
              Suivant
              <ChevronRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              onClick={submit}
              disabled={saving}
              className={`${BOUTON_PIED_CLASSES} bg-[#246990] hover:bg-[#1f5a7a] text-white`}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {saving ? 'Traitement en cours…' : hasSensitiveChanges ? 'Envoyer la demande' : 'Créer la famille'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Hors du Dialog : Radix rend son contenu dans son propre portail, et un
        toast laissé à l'intérieur resterait prisonnier de sa pile. */}
    {error && (
      <ToastPortal>
        {shouldUseLongToast(error, 'error') ? (
          <LongToast show message={error} type="error" onClose={() => setError('')} />
        ) : (
          <Toast message={error} type="error" onClose={() => setError('')} />
        )}
      </ToastPortal>
    )}
    </>
  );
};

export default ModalCreerFamilleDepuisEleves;
